import { Delivery, MatchState, Inning, Player, DismissalType } from "../matchTypes";
import { formatPlayerName } from "./nameFormatter";

const PHRASES: Record<string, string[]> = {
  dot: [
    "{bowler} keeps it tight — no run for {batter}.",
    "Dot ball. {batter} can't find a way through.",
    "{batter} defends and there's no run.",
    "Good delivery from {bowler}, nothing on offer.",
    "{batter} plays it safely — no run.",
    "Another tidy ball from {bowler}. Dot.",
    "{batter} can't beat the field this time.",
    "No run as {bowler} keeps {batter} quiet.",
    "Well contained by {bowler} — dot ball.",
    "{batter} looks for the run but decides against it.",
  ],
  one: [
    "{batter} works it away for a single.",
    "One run added by {batter}.",
    "{batter} finds the gap and takes one.",
    "A comfortable single for {batter}.",
    "{batter} rotates the strike with one.",
    "Nicely placed by {batter} for a single.",
    "{batter} gets off strike with one run.",
    "Quick single taken by {batter}.",
    "{batter} nudges it into space and they take one.",
    "One more to the total as {batter} keeps things moving.",
  ],
  two: [
    "{batter} places it well and they come back for two.",
    "Two runs added by {batter}.",
    "{batter} finds the space and picks up a couple.",
    "Good running — two for {batter}.",
    "{batter} turns one into two.",
    "A well-judged couple from {batter}.",
    "Two more to the total for {batter}.",
    "{batter} keeps the scoreboard moving with two.",
    "Nicely worked away by {batter} for a pair.",
    "{batter} pushes hard and completes two runs.",
  ],
  three: [
    "{batter} finds plenty of space and they run three.",
    "Three runs added by {batter}.",
    "Excellent running from {batter} — three taken.",
    "{batter} keeps them moving and picks up three.",
    "A well-placed shot earns {batter} three.",
    "They push hard and come back for a third.",
    "{batter} collects three valuable runs.",
    "Good placement and running — three to {batter}.",
    "{batter} makes the most of the gap with three.",
    "Three more go onto the scoreboard for {batter}.",
  ],
  four: [
    "FOUR! {batter} finds the boundary.",
    "{batter} puts that away for four.",
    "Beautifully struck by {batter} — four runs.",
    "FOUR! {batter} finds the gap perfectly.",
    "{batter} punishes that delivery for four.",
    "Four more! Excellent shot from {batter}.",
    "{batter} sends it racing away for four.",
    "Cracking shot — four to {batter}.",
    "{batter} gets full value with a boundary.",
    "FOUR! Nicely timed by {batter}.",
  ],
  six: [
    "SIX! {batter} sends it all the way.",
    "Maximum! Six runs for {batter}.",
    "{batter} launches that for six.",
    "SIX! Huge hit from {batter}.",
    "{batter} goes big and clears the boundary.",
    "What a strike — six for {batter}!",
    "Maximum runs as {batter} puts that away.",
    "{batter} connects beautifully — SIX!",
    "That's gone the distance! Six to {batter}.",
    "Powerful hitting from {batter} — six more.",
  ],
  wide: [
    "Wide from {bowler} — an extra added.",
    "{bowler} sends that too wide.",
    "Wide called. One extra to the batting side.",
    "That one gets away from {bowler} — wide.",
    "Too far outside the line from {bowler}. Wide.",
    "An extra conceded by {bowler} with a wide.",
    "Wide ball — {bowler} will have to bowl that one again.",
    "{bowler} misses the line and concedes a wide.",
    "That's outside the allowed line — wide called.",
    "One extra added as {bowler} bowls a wide.",
  ],
  noball: [
    "No-ball from {bowler} — an extra added.",
    "{bowler} oversteps and it's called a no-ball.",
    "No-ball called. An extra goes to the batting side.",
    "Illegal delivery from {bowler} — no-ball.",
    "{bowler} gets that one wrong and concedes a no-ball.",
    "An extra added for the no-ball from {bowler}.",
    "No-ball — that delivery will have to be bowled again.",
    "{bowler} concedes an extra with a no-ball.",
    "The umpire signals no-ball from {bowler}.",
    "No-ball called as {bowler} has to try that delivery again.",
  ],
  caught: [
    "OUT! {batter} is caught off the bowling of {bowler}.",
    "CAUGHT! {batter} has to go.",
    "Wicket! {batter} finds the fielder.",
    "{batter} is caught — a breakthrough for {bowler}.",
    "Taken! {batter} is dismissed.",
    "OUT! The catch is held and {batter} departs.",
    "{batter} goes aerial and the chance is taken.",
    "Wicket for {bowler} as {batter} is caught.",
    "The fielder makes no mistake — {batter} is out.",
    "CAUGHT! {bowler} gets the breakthrough.",
  ],
  bowled: [
    "BOWLED! {bowler} gets through {batter}.",
    "OUT! {batter} is bowled by {bowler}.",
    "{bowler} hits the stumps — {batter} has to go.",
    "Clean bowled! Brilliant delivery from {bowler}.",
    "Wicket! {bowler} knocks the stumps over.",
    "{batter} misses and {bowler} strikes.",
    "The stumps are disturbed — {batter} is bowled.",
    "OUT! {bowler} finds a way through.",
    "Bowled him! {bowler} claims the wicket.",
    "That's through the defence — {batter} is bowled.",
  ],
  lbw: [
    "LBW! {batter} is trapped in front.",
    "{batter} is given LBW.",
    "Huge appeal and the decision is OUT — {batter} is LBW.",
    "{bowler} traps {batter} in front — LBW.",
    "OUT! {batter} is pinned in front.",
    "{batter} is struck on the pad and given LBW.",
    "Up goes the finger — {batter} is out LBW.",
    "{bowler} gets the breakthrough as {batter} is trapped in front.",
    "LBW! {batter} has to go.",
    "{batter} is beaten and adjudged LBW.",
  ],
  run_out: [
    "RUN OUT! {batter} is caught short of the crease.",
    "OUT! A run-out ends {batter}'s stay.",
    "{batter} doesn't make it back — run out.",
    "Sharp fielding results in a run-out.",
    "RUN OUT! The fielding side gets the breakthrough.",
    "{batter} is short of the crease and has to go.",
    "A mix-up costs the wicket — run out.",
    "Excellent fielding and {batter} is run out.",
    "The throw beats {batter} to the crease — OUT.",
    "Run-out! The batting side loses another wicket.",
  ],
  wplus1: [
    "{batter} makes a cheeky run and bags the wall bonus too — 2 runs for him.",
    "{batter} sneaks through for one and picks up the wall bonus — that's 2.",
    "Quick running from {batter}, plus the wall bonus — 2 added.",
    "{batter} completes the run and earns the wall bonus as well — 2 runs.",
    "One taken and the wall bonus added — 2 more for {batter}.",
    "{batter} works it away, gets the run and the wall bonus — 2 to his name.",
    "{batter} gets through for one and collects the wall bonus too — 2 runs.",
    "A sharp single from {batter}, and the wall bonus makes it 2.",
    "{batter} takes the run and adds the wall bonus — 2 from the delivery.",
    "Nicely done by {batter} — one completed plus the wall bonus for 2.",
  ],
  bye: [
    "The ball beats everyone and they run {runs} bye(s).",
    "Through to the keeper area and {runs} bye(s) are taken.",
    "No contact from the bat — {runs} bye(s) added.",
    "The ball gets away and they pick up {runs} bye(s).",
    "{runs} bye(s) go onto the extras.",
    "They take advantage and run {runs} bye(s).",
    "Past the batter and {runs} bye(s) are collected.",
    "The batting side adds {runs} bye(s) without bat contact.",
    "The ball escapes the fielding side and {runs} bye(s) are taken.",
    "{runs} bye(s) added to the total.",
  ],
  legbye: [
    "The ball clips {batter} and they come back for {runs} leg bye(s).",
    "Off the pad and away — {runs} leg bye(s) added.",
    "They scamper through for {runs} leg bye(s).",
    "The ball runs away off the body and {runs} leg bye(s) are taken.",
    "Leg byes added as they complete {runs}.",
    "It comes off {batter} and they pick up {runs} leg bye(s).",
    "Nothing from the bat, but {runs} leg bye(s) go to the total.",
    "They make the most of it and collect {runs} leg bye(s).",
    "Off the pad, and the batting side adds {runs} leg bye(s).",
    "{runs} leg bye(s) added to the extras.",
  ],
  generic_wicket: [
    "OUT! {batter} has been dismissed.",
    "Wicket! {batter} has to go.",
    "{batter}'s innings comes to an end.",
    "OUT! The fielding side makes the breakthrough.",
    "A wicket falls and {batter} departs.",
    "Wicket! A change of batter is needed.",
    "{batter} is dismissed.",
    "Another breakthrough for the fielding side.",
  ],
};

/** A short, machine-readable event label (e.g. "WICKET • BOWLED", "B • 2 BYES").
 *  Preserves ALL relevant attributes of the delivery, including combinations
 *  such as a no-ball with a run-out. Returns "" when no label applies. */
export function deliveryEventLabel(delivery: Delivery): string {
  // Wall bonus (Indoor W+1) is its own event and is never a wicket.
  if (delivery.wallBonus) return "W+1";

  const parts: string[] = [];

  if (delivery.extraType === "wide") parts.push("WD • RE-BOWL");
  else if (delivery.extraType === "noball") parts.push("NB • RE-BOWL");
  else if (delivery.extraType === "bye") parts.push(`B • ${delivery.runs} ${delivery.runs === 1 ? "BYE" : "BYTES"}`);
  else if (delivery.extraType === "legbye") parts.push(`LB • ${delivery.runs} ${delivery.runs === 1 ? "LEG BYE" : "LEG BYES"}`);

  if (delivery.isWicket) {
    const dt = delivery.dismissalType;
    const wicketLabel = dt
      ? `WICKET • ${dismissalLabel(dt)}`
      : "WICKET";
    parts.push(wicketLabel);
  }

  if (parts.length > 0) return parts.join(" • ");

  const runs = delivery.runs;
  if (runs === 0) return "DOT";
  if (runs === 1) return "1 RUN";
  if (runs === 2) return "2 RUNS";
  if (runs === 3) return "3 RUNS";
  if (runs === 4) return "FOUR";
  if (runs === 6) return "SIX";
  return `${runs} RUNS`;
}

function dismissalLabel(dt: DismissalType): string {
  switch (dt) {
    case "Bowled": return "BOWLED";
    case "Caught": return "CAUGHT";
    case "Run Out": return "RUN OUT";
    case "LBW": return "LBW";
    case "Stumped": return "STUMPED";
    case "Hit Wicket": return "HIT WICKET";
    default: return "WICKET";
  }
}

/** Convert a template phrase to a normalised skeleton for comparison.
 *  Strips the {batter}/{bowler}/{runs} placeholders so it can match against
 *  resolved phrases where names/numbers were already substituted. */
function skeleton(phrase: string): string {
  return phrase
    .replace(/\{batter\}/g, "\x00")
    .replace(/\{bowler\}/g, "\x00")
    .replace(/\{runs\}/g, "\x00")
    .replace(/[^a-zA-Z0-9\x00 ]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** Strip any text between two known anchor words so resolved phrases
 *  (with player names) can be matched back to their template. */
function resolveToSkeleton(phrase: string): string {
  return phrase
    .replace(/[^a-zA-Z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function pickPhrase(category: string, avoid: string | null): string {
  const list = PHRASES[category];
  if (!list || list.length === 0) return "";

  if (avoid && list.length > 1) {
    // Build skeletons for all templates and the avoid text so we can
    // match even when `avoid` is a resolved phrase with player names.
    const avoidSk = resolveToSkeleton(avoid);
    const idx = list.findIndex(template => {
      const tmplSk = skeleton(template);
      // Replace template placeholders with a wildcard-style check:
      // compare word-by-word allowing any single word where template has \x00
      const tmplWords = tmplSk.split(" ");
      const avoidWords = avoidSk.split(" ");
      if (tmplWords.length !== avoidWords.length) return false;
      for (let i = 0; i < tmplWords.length; i++) {
        if (tmplWords[i] === "\x00") continue;
        if (tmplWords[i] !== avoidWords[i]) return false;
      }
      return true;
    });
    if (idx !== -1) {
      return list[(idx + 1 + Math.floor(Math.random() * (list.length - 1))) % list.length];
    }
  }
  return list[Math.floor(Math.random() * list.length)];
}

function resolveBatterName(state: MatchState, delivery: Delivery, inning: Inning): string {
  const battingTeam: Player[] = inning.battingTeam === "A" ? state.teamAPlayers : state.teamBPlayers;
  const batter = battingTeam.find(p => p.id === delivery.batterId);
  if (!batter || !batter.name?.trim()) return "the batter";
  return formatPlayerName(batter.name);
}

function resolveBowlerName(state: MatchState, delivery: Delivery, inning: Inning): string {
  const bowlingTeam: Player[] = inning.battingTeam === "A" ? state.teamBPlayers : state.teamAPlayers;
  const bowler = bowlingTeam.find(p => p.id === delivery.bowlerId);
  if (!bowler || !bowler.name?.trim()) return "the bowler";
  return formatPlayerName(bowler.name);
}

/** Categorise a delivery for commentary, preserving ALL relevant attributes.
 *  A combined event (e.g. no-ball + run-out) yields a combined phrase so the
 *  illegal delivery is not hidden by the wicket wording. */
function categorizeDelivery(
  state: MatchState,
  delivery: Delivery,
  inning: Inning
): { category: string; batter: string; bowler: string } {
  const batter = resolveBatterName(state, delivery, inning);
  const bowler = resolveBowlerName(state, delivery, inning);

  // Wall bonus (Indoor W+1) is its own non-wicket event.
  if (delivery.wallBonus) {
    return { category: "wplus1", batter, bowler };
  }

  if (delivery.isWicket) {
    const dt = delivery.dismissalType;
    let wicketCategory = "generic_wicket";
    if (dt === "Caught") wicketCategory = "caught";
    else if (dt === "Bowled") wicketCategory = "bowled";
    else if (dt === "LBW") wicketCategory = "lbw";
    else if (dt === "Run Out") wicketCategory = "run_out";
    return { category: wicketCategory, batter, bowler };
  }

  if (delivery.extraType === "wide") return { category: "wide", batter, bowler };
  if (delivery.extraType === "noball") return { category: "noball", batter, bowler };
  if (delivery.extraType === "bye") return { category: "bye", batter, bowler };
  if (delivery.extraType === "legbye") return { category: "legbye", batter, bowler };

  const runs = delivery.runs;
  if (runs === 0) return { category: "dot", batter, bowler };
  if (runs === 1) return { category: "one", batter, bowler };
  if (runs === 2) return { category: "two", batter, bowler };
  if (runs === 3) return { category: "three", batter, bowler };
  if (runs === 4) return { category: "four", batter, bowler };
  if (runs === 6) return { category: "six", batter, bowler };

  return { category: "dot", batter, bowler };
}

/** Build the full commentary text for a delivery, including combined events.
 *  When a delivery carries both an illegal-extra attribute and a wicket, the
 *  extra wording is prepended so the illegal delivery is never hidden. */
function buildDeliveryPhrase(
  state: MatchState,
  delivery: Delivery,
  inning: Inning,
  avoidText: string | null,
): string {
  const { category, batter, bowler } = categorizeDelivery(state, delivery, inning);

  let phrase = pickPhrase(category, avoidText);
  if (!phrase) phrase = pickPhrase("generic_wicket", avoidText);

  phrase = phrase
    .replace(/\{batter\}/g, batter)
    .replace(/\{bowler\}/g, bowler)
    .replace(/\{runs\}/g, String(delivery.runs));

  // Combined delivery: an illegal extra (no-ball/wide) together with a wicket.
  // Prepend the extra wording so both attributes are reflected in commentary.
  if (delivery.isWicket && (delivery.extraType === "noball" || delivery.extraType === "wide")) {
    const extraPhrase = pickPhrase(delivery.extraType, avoidText)
      .replace(/\{bowler\}/g, bowler)
      .replace(/\{batter\}/g, batter);
    if (extraPhrase) {
      if (delivery.dismissalType === "Run Out") {
        // Run-out on a no-ball/wide: the illegal delivery is re-bowled, but
        // the run-out still stands. Make this explicit.
        phrase = `${extraPhrase} But ${batter} is caught short of the crease — run out.`;
      } else {
        phrase = `${extraPhrase} ${phrase}`;
      }
    }
  }

  return phrase;
}

export interface GeneratedCommentary {
  text: string;
  over: number;
  ball: number;
  deliveryIndex: number;
  inningsIndex: number;
}

export function generateDeliveryCommentary(
  state: MatchState,
  delivery: Delivery,
  deliveryIndex: number,
  inningsIndex: number,
  avoidText: string | null,
): GeneratedCommentary {
  const inning = state.innings[inningsIndex];
  const phrase = buildDeliveryPhrase(state, delivery, inning, avoidText);

  const ballsPerOver = state.ballsPerOver || 6;
  let legalBallsSoFar = 0;
  for (let i = 0; i <= deliveryIndex; i++) {
    if (inning.deliveries[i]?.isLegal) legalBallsSoFar++;
  }
  const over = Math.floor((legalBallsSoFar - (delivery.isLegal ? 1 : 0)) / ballsPerOver);
  const ballInOver = (legalBallsSoFar - (delivery.isLegal ? 1 : 0)) % ballsPerOver;
  const displayBall = delivery.isLegal ? ballInOver + 1 : ballInOver;

  return {
    text: phrase,
    over,
    ball: displayBall,
    deliveryIndex,
    inningsIndex,
  };
}
